import { useState } from "react";
import { useNavigate } from "react-router-dom";
import SeoHead from "@/components/feature/SeoHead";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

const buyerConfirmJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "我的订单 · 货款托管 | 皇冠鸿运商城",
  "description": "皇冠鸿运商城买家订单中心，查看货款托管状态，确认收货放款，发起质量纠纷，保障买家权益。",
  "url": `${SITE_URL}/buyer-confirm`,
  "noIndex": true
};

interface OrderItem {
  id: string;
  product: string;
  image: string;
  spec: string;
  qty: number;
  price: number;
  factory: string;
  factoryVerified: boolean;
}

interface EscrowOrder {
  id: string;
  items: OrderItem[];
  totalAmount: number;
  escrowStatus: "holding" | "released" | "dispute";
  payDate: string;
  shipDate: string;
  deliverDate?: string;
  confirmDeadline: string;
  trackingNo: string;
  carrier: string;
  factoryName: string;
  buyerEmail?: string;
  buyerPhone?: string;
  notified?: boolean;
}

const mockOrders: EscrowOrder[] = [
  {
    id: "ORD-2026041102",
    items: [
      {
        id: "item1",
        product: "极光户外登山背包 60L 专业徒步防水大容量",
        image: "https://public.readdy.ai/ai/img_res/298f853a62c078ffdd0036cf559dfda6.jpg",
        spec: "军绿色 / 60L",
        qty: 1,
        price: 458,
        factory: "极光户外装备有限公司",
        factoryVerified: true,
      }
    ],
    totalAmount: 458,
    escrowStatus: "holding",
    payDate: "2026-04-09 10:23",
    shipDate: "2026-04-09 16:45",
    confirmDeadline: "2026-04-16",
    trackingNo: "YT9876543210",
    carrier: "圆通速递",
    factoryName: "极光户外装备有限公司",
    buyerEmail: "wang**@gmail.com",
    buyerPhone: "+86 138****5678",
    notified: true,
  },
  {
    id: "ORD-2026041104",
    items: [
      {
        id: "item2",
        product: "极光户外防水冲锋衣 男女同款 三合一可拆卸",
        image: "https://readdy.ai/api/search-image?query=outdoor%20waterproof%20jacket%20product%20on%20clean%20white%20background%20professional%20studio%20photography%20high%20quality%20apparel&width=200&height=200&seq=bc2&orientation=squarish",
        spec: "深蓝色 / XL",
        qty: 1,
        price: 688,
        factory: "极光户外装备有限公司",
        factoryVerified: true,
      }
    ],
    totalAmount: 688,
    escrowStatus: "holding",
    payDate: "2026-04-07 09:15",
    shipDate: "2026-04-07 18:30",
    deliverDate: "2026-04-10 14:22",
    confirmDeadline: "2026-04-17",
    trackingNo: "JD5566778899",
    carrier: "京东物流",
    factoryName: "极光户外装备有限公司",
    buyerEmail: "chen**@outlook.com",
    buyerPhone: "+86 139****9012",
    notified: true,
  },
  {
    id: "ORD-2026041101",
    items: [
      {
        id: "item3",
        product: "极光户外防水冲锋衣 男女同款 三合一可拆卸",
        image: "https://readdy.ai/api/search-image?query=outdoor%20waterproof%20jacket%20product%20on%20clean%20white%20background%20professional%20studio%20photography%20high%20quality%20apparel&width=200&height=200&seq=bc3&orientation=squarish",
        spec: "黑色 / L",
        qty: 2,
        price: 688,
        factory: "极光户外装备有限公司",
        factoryVerified: true,
      }
    ],
    totalAmount: 1376,
    escrowStatus: "released",
    payDate: "2026-04-08 11:00",
    shipDate: "2026-04-08 17:20",
    deliverDate: "2026-04-10 10:05",
    confirmDeadline: "2026-04-17",
    trackingNo: "SF1234567890",
    carrier: "顺丰速运",
    factoryName: "极光户外装备有限公司",
    buyerEmail: "li**@163.com",
    buyerPhone: "+86 186****3456",
    notified: true,
  },
];

type Step = "list" | "detail" | "confirm" | "dispute";

// ── Order Summary Panel ───────────────────────────────────────────────────
function OrderSummaryPanel() {
  const [coupon, setCoupon] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);

  const subtotal = 458;
  const shipping = 0;
  const platformFee = 0;
  const discount = couponApplied ? couponDiscount : 0;
  const total = subtotal + shipping + platformFee - discount;

  const validCoupons: Record<string, number> = {
    "CROWN50": 50,
    "NEW100": 100,
    "VIP30": 30,
  };

  const handleApplyCoupon = () => {
    setCouponError("");
    const upper = coupon.trim().toUpperCase();
    if (!upper) { setCouponError("请输入优惠券码"); return; }
    if (validCoupons[upper]) {
      setCouponDiscount(validCoupons[upper]);
      setCouponApplied(true);
    } else {
      setCouponError("优惠券码无效或已过期");
      setCouponApplied(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCoupon("");
    setCouponApplied(false);
    setCouponDiscount(0);
    setCouponError("");
  };

  return (
    <div className="bg-gray-50 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <i className="ri-receipt-line text-gray-500 text-sm"></i>
        <span className="text-gray-700 text-sm font-semibold">订单金额明细</span>
      </div>

      {/* Item row */}
      <div className="px-4 py-3 space-y-2.5">
        <div className="flex items-center gap-3">
          <img
            src="https://readdy.ai/api/search-image?query=hiking%20backpack%2060L%20outdoor%20gear%20clean%20white%20background%20professional%20product%20photography&width=200&height=200&seq=bc1&orientation=squarish"
            alt="商品"
            className="w-10 h-10 rounded-lg object-cover object-top flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="text-gray-700 text-xs font-medium line-clamp-1">极光户外登山背包 60L 专业徒步防水大容量</p>
            <p className="text-gray-400 text-xs mt-0.5">军绿色 / 60L · ×1</p>
          </div>
          <span className="text-gray-800 text-sm font-bold flex-shrink-0">¥458</span>
        </div>

        <div className="border-t border-gray-200 pt-2.5 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">商品小计</span>
            <span className="text-gray-700">¥{subtotal.toLocaleString()}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500 flex items-center gap-1">
              <i className="ri-truck-line"></i>运费
            </span>
            <span className="text-green-600 font-medium">免运费</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500 flex items-center gap-1">
              <i className="ri-shield-check-line"></i>平台托管服务费
            </span>
            <span className="text-green-600 font-medium">免费</span>
          </div>
          {couponApplied && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-red-500 flex items-center gap-1">
                <i className="ri-coupon-3-line"></i>优惠券抵扣
              </span>
              <span className="text-red-500 font-bold">-¥{couponDiscount}</span>
            </div>
          )}
        </div>

        {/* Coupon input */}
        <div className="border-t border-gray-200 pt-3">
          <p className="text-gray-600 text-xs font-medium mb-2 flex items-center gap-1">
            <i className="ri-coupon-3-line text-amber-500"></i>优惠券 / 折扣码
          </p>
          {!couponApplied ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={coupon}
                onChange={e => { setCoupon(e.target.value.toUpperCase()); setCouponError(""); }}
                placeholder="输入优惠券码（如 CROWN50）"
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-amber-400 bg-white font-mono tracking-wider"
              />
              <button
                onClick={handleApplyCoupon}
                className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 rounded-lg text-xs font-bold cursor-pointer whitespace-nowrap transition-colors"
              >
                使用
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              <i className="ri-coupon-3-line text-green-500 text-sm flex-shrink-0"></i>
              <span className="text-green-700 text-xs font-mono font-bold flex-1">{coupon}</span>
              <span className="text-green-600 text-xs font-bold">-¥{couponDiscount}</span>
              <button onClick={handleRemoveCoupon} className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-red-500 cursor-pointer transition-colors flex-shrink-0">
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
          )}
          {couponError && (
            <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
              <i className="ri-error-warning-line"></i>{couponError}
            </p>
          )}
          {!couponApplied && !couponError && (
            <p className="text-gray-400 text-xs mt-1.5">
              <i className="ri-gift-line mr-1 text-amber-400"></i>可用码：CROWN50（减¥50）· NEW100（减¥100）· VIP30（减¥30）
            </p>
          )}
        </div>

        {/* Total */}
        <div className="border-t-2 border-gray-200 pt-3 flex items-center justify-between">
          <span className="text-gray-700 font-bold text-sm">实付金额</span>
          <div className="text-right">
            {couponApplied && (
              <p className="text-gray-400 text-xs line-through">¥{subtotal.toLocaleString()}</p>
            )}
            <span className="text-red-500 font-bold text-xl">¥{total.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Payment Method Card ────────────────────────────────────────────────────
interface PaymentMethod {
  id: string;
  label: string;
  desc: string;
  icon: string;
  iconColor: string;
  bg: string;
  border: string;
  selectedBorder: string;
  selectedBg: string;
}

const PAYMENT_METHODS: PaymentMethod[] = [
  {
    id: "credit",
    label: "信用卡",
    desc: "Visa / Mastercard / UnionPay",
    icon: "ri-bank-card-line",
    iconColor: "text-indigo-600",
    bg: "bg-indigo-50",
    border: "border-gray-200",
    selectedBorder: "border-indigo-500",
    selectedBg: "bg-indigo-50/60",
  },
  {
    id: "debit",
    label: "借记卡",
    desc: "银行储蓄卡 / 借记卡",
    icon: "ri-secure-payment-line",
    iconColor: "text-teal-600",
    bg: "bg-teal-50",
    border: "border-gray-200",
    selectedBorder: "border-teal-500",
    selectedBg: "bg-teal-50/60",
  },
  {
    id: "wechat",
    label: "微信支付",
    desc: "扫码或微信内支付",
    icon: "ri-wechat-pay-line",
    iconColor: "text-green-600",
    bg: "bg-green-50",
    border: "border-gray-200",
    selectedBorder: "border-green-500",
    selectedBg: "bg-green-50/60",
  },
  {
    id: "paypal",
    label: "PayPal",
    desc: "国际买家推荐使用",
    icon: "ri-paypal-line",
    iconColor: "text-sky-600",
    bg: "bg-sky-50",
    border: "border-gray-200",
    selectedBorder: "border-sky-500",
    selectedBg: "bg-sky-50/60",
  },
];

// ── Address types ─────────────────────────────────────────────────────────
interface ShippingAddress {
  id: string;
  name: string;
  phone: string;
  province: string;
  city: string;
  district: string;
  detail: string;
  isDefault: boolean;
}

const MOCK_ADDRESSES: ShippingAddress[] = [
  { id: "addr1", name: "王小明", phone: "138****5678", province: "广东省", city: "深圳市", district: "南山区", detail: "科技园南区 A栋 1201室", isDefault: true },
  { id: "addr2", name: "王小明", phone: "138****5678", province: "北京市", city: "北京市", district: "朝阳区", detail: "望京SOHO T1 2008室", isDefault: false },
];

// ── Place Order Modal ──────────────────────────────────────────────────────
function PlaceOrderModal({ onClose }: { onClose: () => void }) {
  const [modalStep, setModalStep] = useState<"address" | "payment" | "contact" | "processing" | "success">("address");
  const [paymentMethod, setPaymentMethod] = useState<string>("credit");
  const [contactMethod, setContactMethod] = useState<"email" | "phone">("phone");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+86");
  const [showCodes, setShowCodes] = useState(false);
  const [error, setError] = useState("");

  // Address state
  const [addresses, setAddresses] = useState<ShippingAddress[]>(MOCK_ADDRESSES);
  const [selectedAddressId, setSelectedAddressId] = useState<string>("addr1");
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [addrForm, setAddrForm] = useState({ name: "", phone: "", province: "", city: "", district: "", detail: "" });
  const [addrError, setAddrError] = useState("");

  // Card fields
  const [cardNumber, setCardNumber] = useState("");
  const [cardName, setCardName] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvv, setCardCvv] = useState("");

  const countryCodes = [
    { code: "+86", flag: "🇨🇳", name: "中国" },
    { code: "+1",  flag: "🇺🇸", name: "美国" },
    { code: "+44", flag: "🇬🇧", name: "英国" },
    { code: "+81", flag: "🇯🇵", name: "日本" },
    { code: "+65", flag: "🇸🇬", name: "新加坡" },
    { code: "+60", flag: "🇲🇾", name: "马来西亚" },
    { code: "+61", flag: "🇦🇺", name: "澳大利亚" },
    { code: "+971",flag: "🇦🇪", name: "阿联酋" },
  ];

  const selectedPm = PAYMENT_METHODS.find(p => p.id === paymentMethod)!;

  const formatCardNumber = (val: string) => {
    const digits = val.replace(/\D/g, "").slice(0, 16);
    return digits.replace(/(.{4})/g, "$1 ").trim();
  };

  const formatExpiry = (val: string) => {
    const digits = val.replace(/\D/g, "").slice(0, 4);
    if (digits.length >= 3) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return digits;
  };

  const handlePaymentNext = () => {
    setError("");
    if (paymentMethod === "credit" || paymentMethod === "debit") {
      if (!cardNumber || cardNumber.replace(/\s/g, "").length < 16) { setError("请输入完整的卡号"); return; }
      if (!cardName.trim()) { setError("请输入持卡人姓名"); return; }
      if (!cardExpiry || cardExpiry.length < 5) { setError("请输入有效期"); return; }
      if (!cardCvv || cardCvv.length < 3) { setError("请输入安全码"); return; }
    }
    setModalStep("contact");
  };

  const handleContactSubmit = () => {
    setError("");
    if (contactMethod === "email") {
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setError("请输入有效的邮箱地址"); return;
      }
    } else {
      if (!phone || phone.length < 7) {
        setError("请输入有效的手机号码"); return;
      }
    }
    setModalStep("processing");
    setTimeout(() => setModalStep("success"), 2000);
  };

  const handleSaveAddress = () => {
    setAddrError("");
    if (!addrForm.name.trim()) { setAddrError("请输入收货人姓名"); return; }
    if (!addrForm.phone.trim()) { setAddrError("请输入手机号码"); return; }
    if (!addrForm.province || !addrForm.city) { setAddrError("请选择省市"); return; }
    if (!addrForm.detail.trim()) { setAddrError("请输入详细地址"); return; }
    const newAddr: ShippingAddress = {
      id: `addr${Date.now()}`,
      name: addrForm.name,
      phone: addrForm.phone,
      province: addrForm.province,
      city: addrForm.city,
      district: addrForm.district,
      detail: addrForm.detail,
      isDefault: addresses.length === 0,
    };
    setAddresses(prev => [...prev, newAddr]);
    setSelectedAddressId(newAddr.id);
    setShowAddAddress(false);
    setAddrForm({ name: "", phone: "", province: "", city: "", district: "", detail: "" });
  };

  const stepTitles: Record<string, string> = {
    address: "选择收货地址",
    payment: "选择支付方式",
    contact: "填写联系方式",
    processing: "支付处理中",
    success: "下单成功",
  };

  const STEPS = ["address", "payment", "contact"];
  const currentStepIdx = STEPS.indexOf(modalStep);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md mx-4 overflow-hidden max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-orange-500 px-6 py-5 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-white font-bold text-lg">{stepTitles[modalStep]}</h3>
            {modalStep !== "processing" && modalStep !== "success" && (
              <div className="flex items-center gap-1.5 mt-1.5">
                {STEPS.map((s, i) => (
                  <div key={s} className="flex items-center gap-1.5">
                    <div className={`w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold transition-all ${i <= currentStepIdx ? "bg-white text-red-500" : "bg-white/30 text-white"}`}>
                      {i < currentStepIdx ? <i className="ri-check-line text-xs"></i> : i + 1}
                    </div>
                    {i < STEPS.length - 1 && <div className={`w-6 h-0.5 ${i < currentStepIdx ? "bg-white" : "bg-white/30"}`}></div>}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white cursor-pointer flex-shrink-0">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          {/* ── STEP 0: Address ── */}
          {modalStep === "address" && (
            <div className="p-6 space-y-4">
              {!showAddAddress ? (
                <>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setShowAddAddress(true)} className="flex items-center gap-1 text-red-500 hover:text-red-600 text-xs font-medium cursor-pointer transition-colors whitespace-nowrap">
                      <i className="ri-add-line"></i>新增地址
                    </button>
                  </div>

                  <div className="space-y-3">
                    {addresses.map(addr => (
                      <div
                        key={addr.id}
                        onClick={() => setSelectedAddressId(addr.id)}
                        className={`relative flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          selectedAddressId === addr.id
                            ? "border-red-400 bg-red-50/40"
                            : "border-gray-200 hover:border-gray-300 bg-white"
                        }`}
                      >
                        <div className={`w-5 h-5 flex items-center justify-center rounded-full border-2 flex-shrink-0 mt-0.5 transition-all ${
                          selectedAddressId === addr.id ? "border-red-500 bg-red-500" : "border-gray-300"
                        }`}>
                          {selectedAddressId === addr.id && <i className="ri-check-line text-white text-xs"></i>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="text-gray-800 font-bold text-sm">{addr.name}</span>
                            <span className="text-gray-500 text-sm">{addr.phone}</span>
                            {addr.isDefault && (
                              <span className="bg-red-100 text-red-500 text-xs px-1.5 py-0.5 rounded font-medium whitespace-nowrap">默认</span>
                            )}
                          </div>
                          <p className="text-gray-600 text-xs leading-relaxed">
                            {addr.province} {addr.city} {addr.district} {addr.detail}
                          </p>
                        </div>
                        <button
                          onClick={e => { e.stopPropagation(); }}
                          className="text-gray-400 hover:text-gray-600 cursor-pointer flex-shrink-0"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                      </div>
                    ))}

                    {addresses.length === 0 && (
                      <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                        <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
                          <i className="ri-map-pin-line text-gray-400 text-xl"></i>
                        </div>
                        <p className="text-gray-500 text-sm font-medium">暂无收货地址</p>
                        <p className="text-gray-400 text-xs mt-1">请添加收货地址</p>
                      </div>
                    )}
                  </div>

                  <div className="bg-gray-50 rounded-xl px-4 py-3 flex items-center gap-2">
                    <i className="ri-map-pin-2-line text-red-400 text-sm flex-shrink-0"></i>
                    <span className="text-gray-500 text-xs">货物将配送至所选地址，请确认地址准确无误</span>
                  </div>

                  <div className="flex gap-3">
                    <button onClick={onClose} className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-xl text-sm font-medium cursor-pointer hover:bg-gray-50 whitespace-nowrap transition-colors">
                      取消
                    </button>
                    <button
                      onClick={() => { if (selectedAddressId) setModalStep("payment"); }}
                      disabled={!selectedAddressId}
                      className={`flex-1 py-3 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap transition-colors ${selectedAddressId ? "bg-red-500 hover:bg-red-600 text-white" : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}
                    >
                      下一步：选择支付方式 <i className="ri-arrow-right-line ml-1"></i>
                    </button>
                  </div>
                </>
              ) : (
                /* Add new address form */
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <button onClick={() => { setShowAddAddress(false); setAddrError(""); }} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer transition-colors">
                      <i className="ri-arrow-left-line"></i>
                    </button>
                    <p className="text-gray-700 text-sm font-medium">新增收货地址</p>
                  </div>

                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-gray-600 text-xs font-medium block mb-1.5">收货人姓名 <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={addrForm.name}
                          onChange={e => setAddrForm(f => ({ ...f, name: e.target.value }))}
                          placeholder="请输入姓名"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="text-gray-600 text-xs font-medium block mb-1.5">手机号码 <span className="text-red-500">*</span></label>
                        <input
                          type="tel"
                          value={addrForm.phone}
                          onChange={e => setAddrForm(f => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 11) }))}
                          placeholder="请输入手机号"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400 transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-gray-600 text-xs font-medium block mb-1.5">省份 <span className="text-red-500">*</span></label>
                        <select
                          value={addrForm.province}
                          onChange={e => setAddrForm(f => ({ ...f, province: e.target.value }))}
                          className="w-full border border-gray-200 rounded-lg px-2 py-2.5 text-xs outline-none focus:border-red-400 bg-white"
                        >
                          <option value="">省份</option>
                          {["广东省", "北京市", "上海市", "浙江省", "江苏省", "四川省", "湖北省", "福建省", "山东省", "河南省"].map(p => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-gray-600 text-xs font-medium block mb-1.5">城市 <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={addrForm.city}
                          onChange={e => setAddrForm(f => ({ ...f, city: e.target.value }))}
                          placeholder="城市"
                          className="w-full border border-gray-200 rounded-lg px-2 py-2.5 text-xs outline-none focus:border-red-400 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="text-gray-600 text-xs font-medium block mb-1.5">区/县</label>
                        <input
                          type="text"
                          value={addrForm.district}
                          onChange={e => setAddrForm(f => ({ ...f, district: e.target.value }))}
                          placeholder="区/县"
                          className="w-full border border-gray-200 rounded-lg px-2 py-2.5 text-xs outline-none focus:border-red-400 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-gray-600 text-xs font-medium block mb-1.5">详细地址 <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={addrForm.detail}
                        onChange={e => setAddrForm(f => ({ ...f, detail: e.target.value }))}
                        placeholder="街道、楼栋、门牌号等"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400 transition-colors"
                      />
                    </div>
                  </div>

                  {addrError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2">
                      <i className="ri-error-warning-line text-red-500 flex-shrink-0"></i>
                      <p className="text-red-600 text-sm">{addrError}</p>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button onClick={() => { setShowAddAddress(false); setAddrError(""); }} className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-xl text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap transition-colors">
                      取消
                    </button>
                    <button
                      onClick={handleSaveAddress}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className="ri-check-line mr-1"></i>保存地址
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── STEP 1: Payment ── */}
          {modalStep === "payment" && (
            <div className="p-6 space-y-5">
              {/* Security badge */}
              <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-3">
                <i className="ri-shield-check-line text-green-500"></i>
                <span className="text-gray-600 text-xs">所有支付信息均通过 256-bit SSL 加密保护</span>
              </div>

              {/* Order amount summary */}
              <OrderSummaryPanel />

              {/* Payment method grid */}
              <div>
                <p className="text-gray-700 text-sm font-medium mb-3">选择支付方式</p>
                <div className="grid grid-cols-2 gap-3">
                  {PAYMENT_METHODS.map(pm => (
                    <button
                      key={pm.id}
                      onClick={() => { setPaymentMethod(pm.id); setError(""); }}
                      className={`flex flex-col items-start gap-2 p-4 rounded-xl border-2 transition-all cursor-pointer text-left ${
                        paymentMethod === pm.id
                          ? `${pm.selectedBorder} ${pm.selectedBg}`
                          : `${pm.border} hover:border-gray-300 bg-white`
                      }`}
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className={`w-9 h-9 flex items-center justify-center ${pm.bg} rounded-lg`}>
                          <i className={`${pm.icon} ${pm.iconColor} text-lg`}></i>
                        </div>
                        {paymentMethod === pm.id && (
                          <div className="w-5 h-5 flex items-center justify-center bg-red-500 rounded-full">
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-gray-800 font-semibold text-sm">{pm.label}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{pm.desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Card fields */}
              {(paymentMethod === "credit" || paymentMethod === "debit") && (
                <div className="space-y-3 bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-700 text-xs font-semibold mb-1">
                    {paymentMethod === "credit" ? "信用卡" : "借记卡"}信息
                  </p>
                  <div>
                    <label className="text-gray-500 text-xs block mb-1">卡号</label>
                    <div className="relative">
                      <i className="ri-bank-card-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                      <input
                        type="text"
                        value={cardNumber}
                        onChange={e => setCardNumber(formatCardNumber(e.target.value))}
                        placeholder="1234 5678 9012 3456"
                        maxLength={19}
                        className="w-full border border-gray-200 rounded-lg pl-9 pr-4 py-2.5 text-sm outline-none focus:border-red-400 bg-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-500 text-xs block mb-1">持卡人姓名</label>
                    <input
                      type="text"
                      value={cardName}
                      onChange={e => setCardName(e.target.value)}
                      placeholder="ZHANG SAN"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400 bg-white"
                    />
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="text-gray-500 text-xs block mb-1">有效期</label>
                      <input
                        type="text"
                        value={cardExpiry}
                        onChange={e => setCardExpiry(formatExpiry(e.target.value))}
                        placeholder="MM/YY"
                        maxLength={5}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400 bg-white"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="text-gray-500 text-xs block mb-1">安全码 (CVV)</label>
                      <input
                        type="password"
                        value={cardCvv}
                        onChange={e => setCardCvv(e.target.value.replace(/\D/g, "").slice(0, 4))}
                        placeholder="•••"
                        maxLength={4}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400 bg-white"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-3 pt-1">
                    {["ri-visa-line", "ri-mastercard-line", "ri-bank-card-2-line"].map(icon => (
                      <div key={icon} className="w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded-md">
                        <i className={`${icon} text-gray-500 text-base`}></i>
                      </div>
                    ))}
                    <span className="text-gray-400 text-xs">支持主流银行卡</span>
                  </div>
                </div>
              )}

              {/* WeChat QR placeholder */}
              {paymentMethod === "wechat" && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-5 text-center">
                  <div className="w-32 h-32 bg-white border-2 border-green-300 rounded-xl mx-auto mb-3 flex items-center justify-center">
                    <div className="grid grid-cols-5 gap-0.5 p-2">
                      {Array.from({ length: 25 }).map((_, i) => (
                        <div key={i} className={`w-4 h-4 rounded-sm ${[0,1,2,3,4,5,9,10,14,15,19,20,21,22,23,24,6,12,18].includes(i) ? "bg-green-600" : "bg-white"}`}></div>
                      ))}
                    </div>
                  </div>
                  <p className="text-green-700 font-semibold text-sm">微信扫码支付</p>
                  <p className="text-green-600 text-xs mt-1">请使用微信 App 扫描上方二维码完成支付</p>
                  <p className="text-green-500 text-xs mt-0.5">二维码有效期：15分钟</p>
                </div>
              )}

              {/* PayPal redirect notice */}
              {paymentMethod === "paypal" && (
                <div className="bg-sky-50 border border-sky-200 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-sky-100 rounded-xl">
                      <i className="ri-paypal-line text-sky-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sky-800 font-semibold text-sm">PayPal 安全支付</p>
                      <p className="text-sky-600 text-xs">点击下一步后将跳转至 PayPal 完成支付</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-xs text-sky-700">
                    <div className="flex items-center gap-2"><i className="ri-check-line text-sky-500"></i>支持全球 200+ 国家和地区</div>
                    <div className="flex items-center gap-2"><i className="ri-check-line text-sky-500"></i>买家保障计划，交易更安全</div>
                    <div className="flex items-center gap-2"><i className="ri-check-line text-sky-500"></i>支持多种货币自动换算</div>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-500 flex-shrink-0"></i>
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              {/* Selected address summary */}
              {(() => {
                const addr = addresses.find(a => a.id === selectedAddressId);
                return addr ? (
                  <div className="flex items-start gap-3 bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
                    <div className="w-7 h-7 flex items-center justify-center bg-red-50 rounded-lg flex-shrink-0">
                      <i className="ri-map-pin-line text-red-400 text-sm"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-500 text-xs mb-0.5">收货地址</p>
                      <p className="text-gray-700 text-xs font-medium">{addr.name} · {addr.phone}</p>
                      <p className="text-gray-500 text-xs mt-0.5 line-clamp-1">{addr.province} {addr.city} {addr.district} {addr.detail}</p>
                    </div>
                    <button onClick={() => setModalStep("address")} className="text-gray-400 hover:text-red-500 text-xs cursor-pointer whitespace-nowrap transition-colors flex-shrink-0">
                      更改
                    </button>
                  </div>
                ) : null;
              })()}

              <div className="flex gap-3">
                <button onClick={() => setModalStep("address")} className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-xl text-sm font-medium cursor-pointer hover:bg-gray-50 whitespace-nowrap transition-colors">
                  <i className="ri-arrow-left-line mr-1"></i>上一步
                </button>
                <button
                  onClick={handlePaymentNext}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap transition-colors"
                >
                  下一步：填写联系方式 <i className="ri-arrow-right-line ml-1"></i>
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 2: Contact ── */}
          {modalStep === "contact" && (
            <div className="p-6 space-y-5">
              {/* Selected payment summary */}
              <div className={`flex items-center gap-3 ${selectedPm.selectedBg} border ${selectedPm.selectedBorder} rounded-xl px-4 py-3`}>
                <div className={`w-8 h-8 flex items-center justify-center ${selectedPm.bg} rounded-lg flex-shrink-0`}>
                  <i className={`${selectedPm.icon} ${selectedPm.iconColor}`}></i>
                </div>
                <div className="flex-1">
                  <p className="text-gray-700 text-xs font-medium">已选支付方式</p>
                  <p className="text-gray-800 text-sm font-semibold">{selectedPm.label}</p>
                </div>
                <button onClick={() => setModalStep("payment")} className="text-gray-400 hover:text-red-500 text-xs cursor-pointer whitespace-nowrap transition-colors">
                  更改
                </button>
              </div>

              {/* Info banner */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                  <i className="ri-notification-3-line text-amber-600"></i>
                </div>
                <div>
                  <p className="text-amber-800 font-semibold text-sm">物流通知联系方式</p>
                  <p className="text-amber-700 text-xs mt-0.5 leading-relaxed">
                    卖家上传物流单号后，系统将自动发送快递信息到您的邮箱或手机。
                  </p>
                </div>
              </div>

              {/* Method toggle */}
              <div>
                <p className="text-gray-700 text-sm font-medium mb-2">选择通知方式</p>
                <div className="flex bg-gray-100 rounded-xl p-1">
                  {(["phone", "email"] as const).map(m => (
                    <button
                      key={m}
                      onClick={() => { setContactMethod(m); setError(""); }}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                        contactMethod === m ? "bg-white text-gray-900" : "text-gray-400 hover:text-gray-600"
                      }`}
                    >
                      <i className={m === "phone" ? "ri-smartphone-line" : "ri-mail-line"}></i>
                      {m === "phone" ? "手机短信" : "邮箱通知"}
                    </button>
                  ))}
                </div>
              </div>

              {/* Phone input */}
              {contactMethod === "phone" && (
                <div>
                  <label className="text-gray-600 text-xs font-medium block mb-1.5">手机号码 <span className="text-red-500">*</span></label>
                  <div className="flex gap-2">
                    <div className="relative">
                      <button
                        onClick={() => setShowCodes(!showCodes)}
                        className="flex items-center gap-1.5 border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 cursor-pointer hover:border-red-400 transition-colors whitespace-nowrap bg-white"
                      >
                        <span>{countryCodes.find(c => c.code === countryCode)?.flag}</span>
                        <span className="font-medium">{countryCode}</span>
                        <i className="ri-arrow-down-s-line text-gray-400 text-xs"></i>
                      </button>
                      {showCodes && (
                        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl z-20 w-44 max-h-48 overflow-y-auto">
                          {countryCodes.map(c => (
                            <button
                              key={c.code}
                              onClick={() => { setCountryCode(c.code); setShowCodes(false); }}
                              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-red-50 cursor-pointer text-sm text-left transition-colors"
                            >
                              <span>{c.flag}</span>
                              <span className="text-gray-700 flex-1">{c.name}</span>
                              <span className="text-gray-400 text-xs">{c.code}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <input
                      type="tel"
                      value={phone}
                      onChange={e => setPhone(e.target.value.replace(/\D/g, ""))}
                      placeholder="请输入手机号码"
                      className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-red-400 transition-colors"
                    />
                  </div>
                </div>
              )}

              {/* Email input */}
              {contactMethod === "email" && (
                <div>
                  <label className="text-gray-600 text-xs font-medium block mb-1.5">邮箱地址 <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="请输入邮箱地址"
                      className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm outline-none focus:border-red-400 transition-colors"
                    />
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-500 flex-shrink-0"></i>
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              {/* Address summary in contact step */}
              {(() => {
                const addr = addresses.find(a => a.id === selectedAddressId);
                return addr ? (
                  <div className="flex items-start gap-3 bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
                    <div className="w-7 h-7 flex items-center justify-center bg-red-50 rounded-lg flex-shrink-0">
                      <i className="ri-map-pin-line text-red-400 text-sm"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-500 text-xs mb-0.5">收货地址</p>
                      <p className="text-gray-700 text-xs font-medium">{addr.name} · {addr.phone}</p>
                      <p className="text-gray-500 text-xs mt-0.5">{addr.province} {addr.city} {addr.district} {addr.detail}</p>
                    </div>
                  </div>
                ) : null;
              })()}

              <div className="flex gap-3">
                <button onClick={() => { setModalStep("payment"); setError(""); }} className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-xl text-sm font-medium cursor-pointer hover:bg-gray-50 whitespace-nowrap transition-colors">
                  <i className="ri-arrow-left-line mr-1"></i>上一步
                </button>
                <button
                  onClick={handleContactSubmit}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-check-line mr-1"></i>确认支付下单
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 3: Processing ── */}
          {modalStep === "processing" && (
            <div className="p-10 text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-5">
                <div className="w-16 h-16 border-4 border-red-200 border-t-red-500 rounded-full animate-spin"></div>
              </div>
              <p className="text-gray-800 font-bold text-lg mb-2">支付处理中...</p>
              <p className="text-gray-400 text-sm">请稍候，正在通过 {selectedPm.label} 安全支付</p>
              <div className="flex items-center justify-center gap-2 mt-4 text-xs text-gray-400">
                <i className="ri-shield-check-line text-green-500"></i>
                <span>256-bit SSL 加密保护</span>
              </div>
            </div>
          )}

          {/* ── STEP 4: Success ── */}
          {modalStep === "success" && (
            <div className="p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                <i className="ri-check-double-line text-green-500 text-3xl"></i>
              </div>
              <h3 className="text-gray-800 font-bold text-xl mb-2">下单成功！</h3>
              <p className="text-gray-500 text-sm mb-1">订单已提交，货款由平台安全托管</p>
              <div className="bg-gray-50 rounded-xl p-4 text-left mt-4 mb-3 space-y-2">
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 flex items-center justify-center ${selectedPm.bg} rounded-lg flex-shrink-0`}>
                    <i className={`${selectedPm.icon} ${selectedPm.iconColor} text-sm`}></i>
                  </div>
                  <span className="text-gray-700 text-sm font-medium">支付方式：{selectedPm.label}</span>
                  <i className="ri-check-line text-green-500 ml-auto"></i>
                </div>
              </div>
              {/* Address in success */}
              {(() => {
                const addr = addresses.find(a => a.id === selectedAddressId);
                return addr ? (
                  <div className="bg-gray-50 rounded-xl p-4 text-left mt-4 mb-3 space-y-1.5">
                    <p className="text-gray-500 text-xs font-medium flex items-center gap-1"><i className="ri-map-pin-line text-red-400"></i>收货地址</p>
                    <p className="text-gray-700 text-sm font-semibold">{addr.name} · {addr.phone}</p>
                    <p className="text-gray-500 text-xs">{addr.province} {addr.city} {addr.district} {addr.detail}</p>
                  </div>
                ) : null;
              })()}

              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-left mb-6 space-y-2">
                <p className="text-green-700 text-xs font-medium">物流通知将发送至：</p>
                <div className="flex items-center gap-2 text-green-600 text-sm font-bold">
                  <i className={contactMethod === "phone" ? "ri-smartphone-line" : "ri-mail-line"}></i>
                  <span>{contactMethod === "phone" ? `${countryCode} ${phone}` : email}</span>
                </div>
                <p className="text-green-600 text-xs">卖家发货后您将立即收到物流通知</p>
              </div>
              <button onClick={onClose} className="bg-red-500 hover:bg-red-600 text-white px-8 py-3 rounded-xl font-bold text-sm cursor-pointer whitespace-nowrap transition-colors">
                查看我的订单
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EscrowStatusBadge({ status }: { status: EscrowOrder["escrowStatus"] }) {
  const map = {
    holding: { text: "货款托管中", color: "bg-amber-100 text-amber-700", icon: "ri-lock-line" },
    released: { text: "货款已放款", color: "bg-green-100 text-green-700", icon: "ri-check-double-line" },
    dispute: { text: "纠纷处理中", color: "bg-red-100 text-red-600", icon: "ri-error-warning-line" },
  };
  const s = map[status];
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium ${s.color}`}>
      <i className={s.icon}></i>{s.text}
    </span>
  );
}

function OrderCard({ order, onSelect }: { order: EscrowOrder; onSelect: () => void }) {
  const daysLeft = Math.max(0, Math.ceil((new Date(order.confirmDeadline).getTime() - Date.now()) / 86400000));

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-red-200 transition-colors cursor-pointer" onClick={onSelect}>
      <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <span className="text-gray-500 text-xs">订单号：{order.id}</span>
          <EscrowStatusBadge status={order.escrowStatus} />
        </div>
        <span className="text-gray-400 text-xs">下单时间：{order.payDate}</span>
      </div>
      <div className="p-5">
        {order.items.map(item => (
          <div key={item.id} className="flex items-center gap-4">
            <img src={item.image} alt={item.product} className="w-16 h-16 rounded-lg object-cover object-top flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-gray-700 text-sm font-medium line-clamp-2">{item.product}</p>
              <p className="text-gray-400 text-xs mt-1">{item.spec} · {item.qty}件</p>
              <div className="flex items-center gap-1.5 mt-1">
                <i className="ri-verified-badge-fill text-green-500 text-xs"></i>
                <span className="text-green-600 text-xs">{item.factory}</span>
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-red-500 font-bold">¥{(item.price * item.qty).toLocaleString()}</p>
            </div>
          </div>
        ))}
        <div className="mt-4 pt-4 border-t border-gray-50 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4 text-xs text-gray-400 flex-wrap">
            <span><i className="ri-truck-line mr-1"></i>{order.carrier} {order.trackingNo}</span>
            {order.deliverDate && <span><i className="ri-map-pin-line mr-1"></i>已送达 {order.deliverDate}</span>}
          </div>
          <div className="flex items-center gap-3">
            {order.escrowStatus === "holding" && (
              <span className="text-orange-500 text-xs font-medium">
                <i className="ri-time-line mr-1"></i>
                {daysLeft > 0 ? `${daysLeft}天后自动确认` : "今日自动确认"}
              </span>
            )}
            <span className="text-gray-700 font-bold">合计：¥{order.totalAmount.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function OrderDetail({
  order,
  onConfirm,
  onDispute,
  onBack,
}: {
  order: EscrowOrder;
  onConfirm: () => void;
  onDispute: () => void;
  onBack: () => void;
}) {
  const [qualityRating, setQualityRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);

  const timeline = [
    { label: "买家付款", time: order.payDate, done: true, icon: "ri-bank-card-line" },
    { label: "平台托管货款", time: order.payDate, done: true, icon: "ri-lock-line" },
    { label: "工厂发货", time: order.shipDate, done: true, icon: "ri-truck-line" },
    { label: "买家收货", time: order.deliverDate || "待收货", done: !!order.deliverDate, icon: "ri-map-pin-line" },
    { label: "确认质量放款", time: order.escrowStatus === "released" ? "已完成" : "待操作", done: order.escrowStatus === "released", icon: "ri-check-double-line" },
  ];

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1.5 text-gray-500 hover:text-red-500 cursor-pointer text-sm mb-5 transition-colors">
        <i className="ri-arrow-left-line"></i> 返回订单列表
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: order info */}
        <div className="lg:col-span-2 space-y-5">
          {/* Escrow status banner */}
          <div className={`rounded-xl p-5 ${order.escrowStatus === "released" ? "bg-green-50 border border-green-200" : order.escrowStatus === "holding" ? "bg-amber-50 border border-amber-200" : "bg-red-50 border border-red-200"}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-full ${order.escrowStatus === "released" ? "bg-green-500" : order.escrowStatus === "holding" ? "bg-amber-500" : "bg-red-500"} text-white`}>
                <i className={`text-lg ${order.escrowStatus === "released" ? "ri-check-double-line" : order.escrowStatus === "holding" ? "ri-lock-line" : "ri-error-warning-line"}`}></i>
              </div>
              <div>
                <p className={`font-bold text-base ${order.escrowStatus === "released" ? "text-green-700" : order.escrowStatus === "holding" ? "text-amber-700" : "text-red-700"}`}>
                  {order.escrowStatus === "released" ? "货款已成功放款给工厂" : order.escrowStatus === "holding" ? "货款由平台安全托管中" : "货款冻结，纠纷处理中"}
                </p>
                <p className={`text-sm mt-0.5 ${order.escrowStatus === "released" ? "text-green-600" : order.escrowStatus === "holding" ? "text-amber-600" : "text-red-600"}`}>
                  {order.escrowStatus === "released"
                    ? "感谢您的确认，货款已于确认后T+1工作日到账工厂"
                    : order.escrowStatus === "holding"
                    ? `请确认收货质量后点击放款，或在 ${order.confirmDeadline} 前发起纠纷`
                    : "平台正在介入调查，请耐心等待处理结果"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm font-bold">
              <span className={order.escrowStatus === "released" ? "text-green-700" : "text-amber-700"}>托管金额：</span>
              <span className={`text-2xl ${order.escrowStatus === "released" ? "text-green-600" : "text-amber-600"}`}>¥{order.totalAmount.toLocaleString()}</span>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="font-bold text-gray-800 mb-4">货款托管流程</h3>
            <div className="space-y-4">
              {timeline.map((step, idx) => (
                <div key={step.label} className="flex items-start gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 ${step.done ? "bg-green-500 text-white" : "bg-gray-100 text-gray-400"}`}>
                      <i className={`${step.icon} text-sm`}></i>
                    </div>
                    {idx < timeline.length - 1 && (
                      <div className={`w-0.5 h-6 mt-1 ${step.done ? "bg-green-300" : "bg-gray-200"}`}></div>
                    )}
                  </div>
                  <div className="flex-1 pt-1">
                    <p className={`text-sm font-medium ${step.done ? "text-gray-800" : "text-gray-400"}`}>{step.label}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{step.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order items */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="font-bold text-gray-800 mb-4">订单商品</h3>
            {order.items.map(item => (
              <div key={item.id} className="flex items-center gap-4">
                <img src={item.image} alt={item.product} className="w-20 h-20 rounded-xl object-cover object-top flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-gray-800 font-medium text-sm">{item.product}</p>
                  <p className="text-gray-400 text-xs mt-1">{item.spec}</p>
                  <div className="flex items-center gap-1.5 mt-2">
                    <i className="ri-verified-badge-fill text-green-500 text-sm"></i>
                    <span className="text-green-600 text-xs font-medium">{item.factory} · 认证工厂直营</span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-red-500 font-bold text-lg">¥{item.price.toLocaleString()}</p>
                  <p className="text-gray-400 text-xs">× {item.qty}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Logistics */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="font-bold text-gray-800 mb-4">物流信息</h3>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 flex items-center justify-center bg-red-50 rounded-lg">
                <i className="ri-truck-line text-red-500"></i>
              </div>
              <div>
                <p className="text-gray-700 text-sm font-medium">{order.carrier}</p>
                <p className="text-gray-400 text-xs">{order.trackingNo}</p>
              </div>
            </div>
            {/* Notification sent badge */}
            {order.notified && (order.buyerEmail || order.buyerPhone) && (
              <div className="mb-4 bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-lg flex-shrink-0">
                  <i className="ri-notification-3-line text-green-600 text-sm"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-green-700 font-semibold text-xs">物流通知已发送</p>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    {order.buyerEmail && (
                      <span className="text-green-600 text-xs flex items-center gap-1">
                        <i className="ri-mail-line"></i>{order.buyerEmail}
                      </span>
                    )}
                    {order.buyerPhone && (
                      <span className="text-green-600 text-xs flex items-center gap-1">
                        <i className="ri-smartphone-line"></i>{order.buyerPhone}
                      </span>
                    )}
                  </div>
                </div>
                <i className="ri-check-double-line text-green-500 flex-shrink-0"></i>
              </div>
            )}

            <div className="space-y-2">
              {[
                { time: order.deliverDate || "配送中", event: "快件已送达，请注意查收", done: !!order.deliverDate },
                { time: order.shipDate, event: "快件已从工厂仓库发出", done: true },
                { time: order.payDate, event: "工厂已接单，准备发货", done: true },
              ].map((log, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${log.done ? "bg-green-500" : "bg-gray-300"}`}></div>
                  <div>
                    <p className={`text-sm ${log.done ? "text-gray-700" : "text-gray-400"}`}>{log.event}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{log.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: action panel */}
        <div className="space-y-4">
          {/* Quality rating */}
          {order.escrowStatus === "holding" && order.deliverDate && (
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="font-bold text-gray-800 mb-3">商品质量评分</h3>
              <p className="text-gray-400 text-xs mb-3">请根据实际收到的商品质量进行评分</p>
              <div className="flex items-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setQualityRating(star)}
                    className="w-8 h-8 flex items-center justify-center cursor-pointer transition-colors"
                  >
                    <i className={`text-2xl ${star <= (hoverRating || qualityRating) ? "ri-star-fill text-amber-400" : "ri-star-line text-gray-300"}`}></i>
                  </button>
                ))}
                {qualityRating > 0 && (
                  <span className="text-amber-500 text-sm font-medium ml-2">
                    {["", "很差", "较差", "一般", "满意", "非常满意"][qualityRating]}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Confirm release */}
          {order.escrowStatus === "holding" && (
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="font-bold text-gray-800 mb-2">确认放款</h3>
              <p className="text-gray-400 text-xs mb-4 leading-relaxed">
                确认收到商品且质量符合要求后，点击下方按钮释放货款给工厂。货款将在T+1工作日到账工厂。
              </p>
              <div className="bg-amber-50 rounded-lg p-3 text-xs text-amber-700 mb-4">
                <i className="ri-information-line mr-1"></i>
                放款后不可撤销。如有质量问题，请先发起纠纷。
              </div>
              <button
                onClick={onConfirm}
                className="w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-xl font-bold text-sm cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2"
              >
                <i className="ri-check-double-line"></i>
                确认收货并放款 ¥{order.totalAmount.toLocaleString()}
              </button>
            </div>
          )}

          {/* Dispute */}
          {order.escrowStatus === "holding" && (
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="font-bold text-gray-800 mb-2">发起质量纠纷</h3>
              <p className="text-gray-400 text-xs mb-4 leading-relaxed">
                如商品质量与描述不符，可在收货后7天内发起纠纷，平台将介入调查并冻结货款。
              </p>
              <button
                onClick={onDispute}
                className="w-full border-2 border-red-400 text-red-500 hover:bg-red-50 py-3 rounded-xl font-bold text-sm cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2"
              >
                <i className="ri-error-warning-line"></i>
                发起质量纠纷
              </button>
            </div>
          )}

          {order.escrowStatus === "released" && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-5 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-green-500 rounded-full mx-auto mb-3">
                <i className="ri-check-double-line text-white text-xl"></i>
              </div>
              <p className="text-green-700 font-bold">交易已完成</p>
              <p className="text-green-600 text-xs mt-1">货款已成功放款给工厂</p>
            </div>
          )}

          {/* Protection info */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="text-gray-700 font-medium text-sm mb-3 flex items-center gap-1.5">
              <i className="ri-shield-check-line text-red-400"></i>
              买家权益保障
            </h4>
            <ul className="space-y-2 text-xs text-gray-500">
              <li className="flex items-start gap-1.5"><i className="ri-checkbox-circle-line text-green-500 mt-0.5 flex-shrink-0"></i>货款全程平台托管，工厂无法提前取款</li>
              <li className="flex items-start gap-1.5"><i className="ri-checkbox-circle-line text-green-500 mt-0.5 flex-shrink-0"></i>收货7天内可发起质量纠纷</li>
              <li className="flex items-start gap-1.5"><i className="ri-checkbox-circle-line text-green-500 mt-0.5 flex-shrink-0"></i>平台专业团队介入调查纠纷</li>
              <li className="flex items-start gap-1.5"><i className="ri-checkbox-circle-line text-green-500 mt-0.5 flex-shrink-0"></i>仅认证工厂自有品牌商品，杜绝仿冒</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfirmSuccess({ order, onBack }: { order: EscrowOrder; onBack: () => void }) {
  return (
    <div className="max-w-lg mx-auto text-center py-16">
      <div className="w-20 h-20 flex items-center justify-center bg-green-500 rounded-full mx-auto mb-5">
        <i className="ri-check-double-line text-white text-4xl"></i>
      </div>
      <h2 className="text-gray-800 font-bold text-2xl mb-2">放款成功！</h2>
      <p className="text-gray-500 text-sm mb-1">您已确认收货并释放货款</p>
      <p className="text-green-500 font-bold text-3xl my-4">¥{order.totalAmount.toLocaleString()}</p>
      <p className="text-gray-400 text-xs mb-8">货款将在T+1工作日内到账工厂账户</p>
      <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-left mb-6">
        <p className="text-green-700 text-sm font-medium mb-2">交易完成确认</p>
        <div className="space-y-1.5 text-xs text-green-600">
          <p><i className="ri-check-line mr-1"></i>订单号：{order.id}</p>
          <p><i className="ri-check-line mr-1"></i>工厂：{order.factoryName}</p>
          <p><i className="ri-check-line mr-1"></i>放款金额：¥{order.totalAmount.toLocaleString()}</p>
          <p><i className="ri-check-line mr-1"></i>预计到账：T+1工作日</p>
        </div>
      </div>
      <button
        onClick={onBack}
        className="bg-red-500 hover:bg-red-600 text-white px-8 py-3 rounded-xl font-bold text-sm cursor-pointer whitespace-nowrap transition-colors"
      >
        返回订单列表
      </button>
    </div>
  );
}

function DisputeModal({ order, onClose }: { order: EscrowOrder; onClose: () => void }) {
  const [reason, setReason] = useState("");
  const [desc, setDesc] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const reasons = ["商品质量与描述严重不符", "收到假冒伪劣商品", "商品存在安全隐患", "商品损坏/缺件", "其他质量问题"];

  const handleSubmit = () => {
    if (!reason) return;
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-2xl p-8 w-full max-w-md mx-4 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-full mx-auto mb-4">
            <i className="ri-error-warning-line text-orange-500 text-3xl"></i>
          </div>
          <h3 className="font-bold text-gray-800 text-xl mb-2">纠纷已提交</h3>
          <p className="text-gray-500 text-sm mb-1">货款已冻结，平台将在48小时内介入调查</p>
          <p className="text-gray-400 text-xs mb-6">纠纷编号：DSP-{order.id}</p>
          <button onClick={onClose} className="bg-red-500 hover:bg-red-600 text-white px-8 py-3 rounded-xl font-bold text-sm cursor-pointer whitespace-nowrap hover:bg-red-600 transition-colors">
            我知道了
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="bg-red-500 px-6 py-4 flex items-center justify-between">
          <h3 className="text-white font-bold text-lg">发起质量纠纷</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">
            <i className="ri-information-line mr-1"></i>
            提交纠纷后，货款将立即冻结，平台将在48小时内介入调查。请如实描述问题。
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">纠纷原因 <span className="text-red-500">*</span></label>
            <div className="space-y-2">
              {reasons.map(r => (
                <label key={r} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="reason"
                    value={r}
                    checked={reason === r}
                    onChange={() => setReason(r)}
                    className="text-red-500"
                  />
                  <span className="text-gray-700 text-sm">{r}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-1.5">详细描述</label>
            <textarea
              value={desc}
              onChange={e => setDesc(e.target.value.slice(0, 500))}
              placeholder="请详细描述商品质量问题，有助于平台快速处理..."
              rows={4}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400 resize-none"
            />
            <p className="text-gray-400 text-xs text-right mt-1">{desc.length}/500</p>
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-1.5">上传凭证图片</label>
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center cursor-pointer hover:border-red-300 transition-colors">
              <i className="ri-image-add-line text-gray-400 text-2xl mb-1"></i>
              <p className="text-gray-400 text-xs">点击上传商品问题照片（最多6张）</p>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button onClick={onClose} className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap transition-colors">
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={!reason}
            className={`flex-1 py-2.5 rounded-lg text-sm font-bold cursor-pointer whitespace-nowrap transition-colors ${reason ? "bg-red-500 hover:bg-red-600 text-white" : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}
          >
            提交纠纷
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BuyerConfirmPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("list");
  const [selectedOrder, setSelectedOrder] = useState<EscrowOrder | null>(null);
  const [showDispute, setShowDispute] = useState(false);
  const [showPlaceOrder, setShowPlaceOrder] = useState(false);
  const [filter, setFilter] = useState<"all" | EscrowOrder["escrowStatus"]>("all");

  const filtered = filter === "all" ? mockOrders : mockOrders.filter(o => o.escrowStatus === filter);

  const handleConfirm = () => {
    setStep("confirm");
  };

  const handleDispute = () => {
    setShowDispute(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SeoHead
        title="我的订单 · 货款托管 | 皇冠鸿运商城"
        description="皇冠鸿运商城买家订单中心，查看货款托管状态，确认收货放款，发起质量纠纷，保障买家权益。"
        keywords="买家订单,货款托管,确认收货,质量纠纷,皇冠鸿运商城"
        canonical="/buyer-confirm"
        jsonLd={buyerConfirmJsonLd}
      />
      {/* Top bar */}
      <div className="bg-red-600 text-white text-xs py-1.5 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <span>皇冠鸿运商城 — 买家中心</span>
          <a href="/" className="hover:underline cursor-pointer flex items-center gap-1"><i className="ri-home-line"></i> 返回首页</a>
        </div>
      </div>

      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-4 py-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-bold text-2xl mb-1">我的订单 · 货款托管</h1>
          <p className="text-white/70 text-sm">所有订单货款由平台安全托管，确认质量后方可放款给工厂</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {step === "list" && (
          <div>
            {/* Place order CTA */}
            <div className="bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 rounded-2xl p-5 mb-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gray-900/15 rounded-xl flex-shrink-0">
                  <i className="ri-shopping-bag-line text-gray-900 text-2xl"></i>
                </div>
                <div>
                  <p className="text-gray-900 font-bold text-base">下单时留下邮箱或手机号</p>
                  <p className="text-gray-800/70 text-sm">卖家发货后系统自动发送物流通知，实时掌握包裹动态</p>
                </div>
              </div>
              <button
                onClick={() => setShowPlaceOrder(true)}
                className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-3 rounded-xl font-bold text-sm cursor-pointer whitespace-nowrap transition-colors flex-shrink-0"
              >
                <i className="ri-add-circle-line mr-1.5"></i>立即下单
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              {[
                { label: "托管中", count: mockOrders.filter(o => o.escrowStatus === "holding").length, amount: mockOrders.filter(o => o.escrowStatus === "holding").reduce((s, o) => s + o.amount, 0), color: "text-amber-500", bg: "bg-amber-50", icon: "ri-lock-line" },
                { label: "已放款", count: mockOrders.filter(o => o.escrowStatus === "released").length, amount: mockOrders.filter(o => o.escrowStatus === "released").reduce((s, o) => s + o.amount, 0), color: "text-green-500", bg: "bg-green-50", icon: "ri-check-double-line" },
                { label: "纠纷中", count: mockOrders.filter(o => o.escrowStatus === "dispute").length, amount: mockOrders.filter(o => o.escrowStatus === "dispute").reduce((s, o) => s + o.amount, 0), color: "text-red-500", bg: "bg-red-50", icon: "ri-error-warning-line" },
              ].map(s => (
                <div key={s.label} className="bg-white rounded-xl p-4 border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-8 h-8 flex items-center justify-center ${s.bg} rounded-lg`}>
                      <i className={`${s.icon} ${s.color}`}></i>
                    </div>
                    <span className="text-gray-500 text-sm">{s.label}</span>
                  </div>
                  <p className={`text-xl font-bold ${s.color}`}>¥{s.amount.toLocaleString()}</p>
                  <p className="text-gray-400 text-xs mt-0.5">{s.count} 笔订单</p>
                </div>
              ))}
            </div>

            {/* Filter */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {[
                { key: "all", label: "全部订单" },
                { key: "holding", label: "托管中" },
                { key: "released", label: "已放款" },
                { key: "dispute", label: "纠纷中" },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setFilter(tab.key as typeof filter)}
                  className={`px-4 py-1.5 rounded-full text-sm cursor-pointer whitespace-nowrap transition-colors ${filter === tab.key ? "bg-red-500 text-white" : "bg-white text-gray-600 border border-gray-200 hover:border-red-300"}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="space-y-4">
              {filtered.map(order => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onSelect={() => { setSelectedOrder(order); setStep("detail"); }}
                />
              ))}
            </div>
          </div>
        )}

        {step === "detail" && selectedOrder && (
          <OrderDetail
            order={selectedOrder}
            onConfirm={handleConfirm}
            onDispute={handleDispute}
            onBack={() => setStep("list")}
          />
        )}

        {step === "confirm" && selectedOrder && (
          <ConfirmSuccess order={selectedOrder} onBack={() => { setStep("list"); setSelectedOrder(null); }} />
        )}
      </div>

      {showDispute && selectedOrder && (
        <DisputeModal order={selectedOrder} onClose={() => { setShowDispute(false); setStep("list"); }} />
      )}
      {showPlaceOrder && (
        <PlaceOrderModal onClose={() => setShowPlaceOrder(false)} />
      )}
    </div>
  );
}
